import { useId, type HTMLAttributes } from 'react';
import { Input } from '@ui/input.tsx';
import { cn } from '@r/utils/utils.ts';

type RangeInputProps = {
  minValue: string;
  maxValue: string;
  onMinChange: (value: string) => void;
  onMaxChange: (value: string) => void;
  /** Libellé accessible et placeholder par défaut du champ minimum. */
  minLabel?: string;
  /** Libellé accessible et placeholder par défaut du champ maximum. */
  maxLabel?: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
  autoFocus?: boolean;
  /** Message d'erreur affiché sous les champs ; passe aussi les inputs en état invalide. */
  error?: string;
  className?: string;
  inputClassName?: string;
};

/**
 * Double input pour saisir une borne minimum et maximum.
 * Composant contrôlé : le parsing, la validation et la persistance restent à la charge de l'appelant.
 */
const RangeInput = ({
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  minLabel = 'Min',
  maxLabel = 'Max',
  minPlaceholder,
  maxPlaceholder,
  inputMode = 'numeric',
  autoFocus,
  error,
  className,
  inputClassName
}: RangeInputProps) => {
  const errorId = useId();
  const hasError = Boolean(error);
  const sharedProps = {
    inputMode,
    className: inputClassName,
    error: hasError,
    'aria-invalid': hasError,
    'aria-describedby': hasError ? errorId : undefined
  };

  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex gap-2 items-center">
        <Input
          {...sharedProps}
          aria-label={minLabel}
          placeholder={minPlaceholder ?? minLabel}
          value={minValue}
          onChange={(event) => onMinChange(event.target.value)}
          autoFocus={autoFocus}
        />
        <Input
          {...sharedProps}
          aria-label={maxLabel}
          placeholder={maxPlaceholder ?? maxLabel}
          value={maxValue}
          onChange={(event) => onMaxChange(event.target.value)}
        />
      </div>

      {hasError && (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
};

export { RangeInput };
